import React, {createContext, useContext, useState, useCallback, useEffect} from 'react';
import {v4 as uuidv4} from 'uuid';

// 1. Create Context
const NotificationContext = createContext();

// 2. Provider component
export function NotificationProvider({children}) {
    const [notifications, setNotifications] = useState([]);

    const showNotification = useCallback(({type, title, message}) => {
        const id = uuidv4();
        setNotifications((prev) => [...prev, {id, type, title, message}]);
        // auto-dismiss after 5s
        setTimeout(() => {
            setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, 3000);
    }, []);

    // Helper methods
    const showError = (message, title = 'Error') => showNotification({type: 'error', title, message});
    const showSuccess = (message, title = 'Success') => showNotification({type: 'success', title, message});
    const showWarning = (message, title = 'Warning') => showNotification({type: 'warning', title, message});
    const showInfo = (message, title = 'Info') => showNotification({type: 'info', title, message});

    // 3. Global fetch interceptor to notify on network/backend errors
    useEffect(() => {
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);
                if (!response.ok) {
                    // Notify for server errors (5xx or 4xx)
                    showError(`Server error: ${response.status} ${response.statusText}`);
                }
                return response;
            } catch (err) {
                // Network-level or backend-down errors
                showError('Ei õnnestu serveriga ühenduda. Kontrollige võrku või proovige hiljem uuesti.');
                throw err;
            }
        };
        return () => {
            window.fetch = originalFetch;
        };
    }, [showError]);

    return (
        <NotificationContext.Provider value={{showError, showSuccess, showWarning, showInfo}}>
            {children}
            {/* 4. Render notifications */}
            <div className="fixed top-4 right-4 flex flex-col space-y-2 z-50">
                {notifications.map(({id, type, title, message}) => {
                    const typeClass = {
                        error: 'bg-red-500 text-white',
                        success: 'bg-green-500 text-white',
                        warning: 'bg-yellow-500 text-black',
                        info: 'bg-blue-500 text-white'
                    }[type] || 'bg-gray-800 text-white';

                    return (
                        <div key={id} className={`p-4 rounded shadow-md ${typeClass}`}>
                            <strong>{title}</strong>
                            <div>{message}</div>
                        </div>
                    );
                })}
            </div>
        </NotificationContext.Provider>
    );
}

export const useNotification = () => useContext(NotificationContext);
